/* eslint-disable no-unused-expressions */
import React, { useEffect, useState } from 'react';
import { PanelMenu } from 'primereact/panelmenu';
import { classNames } from 'primereact/utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import './Sidebar.scss';

import usePermissions from '../../hooks/usePermissions';
import { useRoleContext } from '../../hooks/useRoleContext';

import NewDashBoardMenuIcon from '../../assets/icons/DashboardNew.svg';
import AIPlaygroundMenuIcon from '../../assets/icons/AI-Playground.svg';
import IntakeMenuIcon from '../../assets/icons/Workflows.svg';
import AssetMenuIcon from '../../assets/icons/Asset-library.svg';
import ReportingMenuIcon from '../../assets/icons/reporting-menu.svg';
import HelpCenterMenuIcon from '../../assets/icons/Help.svg';
import AIPortalMenuIcon from '../../assets/icons/AI-Portal.svg';

const Sidebar = () => {
  const { role, aiPrivileges } = useRoleContext();
  const { hasPermission } = usePermissions();
  const [collapseSidebar, setCollapseSidebar] = useState(false);
  const location = useLocation();
  const pathArr = location.pathname?.split('/');
  const currPathName = pathArr[1];

  const [activeItem, setActiveItem] = useState(currPathName);
  const navigate = useNavigate();

  const withTooltip = (icon, label, id) => {
    const formattedLabel = collapseSidebar
      ? label.split(' ').join('\n')
      : label;
    return collapseSidebar ? (
      <span data-tooltip-id={`tooltip-${id}`} data-tooltip-content={formattedLabel} style={{ display: 'inline-block' }} data-tooltip-place="right">
        {icon}
        <Tooltip
          id={`tooltip-${id}`}
          place="right"
          float
          style={{
            zIndex: 2147483647,
            fontSize: '7.5px',
            padding: '4px 7px',
            wordBreak: 'break-word',
            whiteSpace: 'pre-line',
            lineHeight: '1.7',
            maxWidth: '75px'
          }}
        />
      </span>
    ) : icon;
  };

  const items = [
    {
      id: 'toggle-collapse',
      label: collapseSidebar ? '' : '',
      icon: (
        <i
          className={`pi ${collapseSidebar ? 'pi-angle-double-right' : 'pi-angle-double-left'}`}
          style={{
            fontSize: '1.5rem',
            width: '1.5rem',
            minWidth: '1.5rem',
            textAlign: 'center',
            color: 'white',
            marginRight: '0.5rem'
          }}
        />
      ),
      command: () => setCollapseSidebar(prev => !prev),
      className: 'collapse-toggle-item',
      visible: true
    },
    {
      id: 'home',
      label: (
        <span style={{
          fontFamily: 'Inter',
          fontWeight: 500,
          fontSize: '14px',
          paddingLeft: '3px'
        }}
        >
          Dashboard
        </span>
      ),
      icon: withTooltip(
        <img
          className="mr-2"
          style={{ height: '18px', width: '18px' }}
          alt="dropdown icon"
          src={NewDashBoardMenuIcon}
        />,
        'Dashboard',
        'home'
      ),
      command: () => {
        setActiveItem('home');
        navigate('/home');
      },
      className: currPathName === 'home' && 'active',
      visible: hasPermission('menu_dashboard', role)
    },
    {
      id: 'ai-playground',
      label: (
        <span style={{
          fontFamily: 'Inter',
          fontWeight: 500,
          fontSize: '14px'
        }}
        >
          AI Pre-Check
        </span>
      ),

      icon: withTooltip(<img className="mr-2" style={{ height: '20px', width: '20px' }} alt="dropdown icon" src={AIPortalMenuIcon} />, 'AI Pre-Check', 'ai-playground'),
      command: () => {
        setActiveItem('ai-playground');
        navigate('/ai-playground');
      },
      className: currPathName === 'ai-playground' && 'active',
      visible: hasPermission('menu_aiplayground', role)
    },
    {
      id: 'workflows',
      label: (
        <span style={{
          fontFamily: 'Inter',
          fontWeight: 500,
          fontSize: '14px'
        }}
        >
          Workflows
        </span>
      ),
      icon: withTooltip(
        <img className="mr-2" style={{ height: '20px', width: '20px' }} alt="dropdown icon" src={IntakeMenuIcon} />,
        'Workflows',
        'workflows'
      ),
      command: () => {
        setActiveItem('workflows');
        if (role === 'reviewer') {
          navigate('/workflows/list');
        } else if (['admin', 'marcom', 'agency', 'brand_owner'].includes(role)) {
          navigate('/workflows/list');
        } else {
          navigate('/workflows/list');
        }
      },
      className: currPathName === 'workflows' && 'active',
      visible: hasPermission('menu_workflows', role)
    },
    {
      id: 'assets-library',
      label: (
        <span style={{
          fontFamily: 'Inter',
          fontWeight: 500,
          fontSize: '14px'
        }}
        >
          Assets Library
        </span>
      ),
      icon: withTooltip(<img className="mr-2" style={{ height: '20px', width: '20px' }} alt="dropdown icon" src={AssetMenuIcon} />, 'Assets Library', 'assets-library'),
      command: () => {
        if (currPathName === 'assets-library') {
          navigate(0);
        } else {
          setActiveItem('assets-library');
          navigate('/assets-library');
        }
      },
      className: currPathName === 'assets-library' && 'active',
      visible: hasPermission('menu_assets', role)
    },
    {
      id: 'ai-portal',
      label: (
        <span style={{
          fontFamily: 'Inter',
          fontWeight: 500,
          fontSize: '14px'
        }}
        >
          AI Portal
        </span>
      ),
      icon: withTooltip(<img className="mr-2" style={{ height: '20px', width: '20px' }} alt="dropdown icon" src={AIPlaygroundMenuIcon} />, 'AI Portal', 'ai-portal'),
      command: () => {
        setActiveItem('ai-portal');
        navigate('/ai-portal/collection');
      },
      className: currPathName === 'ai-portal' && 'active',
      visible: ['admin', 'marcom'].includes(role) || ['admin'].includes(aiPrivileges)
    },
    {
      id: 'reporting',
      label: (
        <span style={{
          fontFamily: 'Inter',
          fontWeight: 500,
          fontSize: '14px'
        }}
        >
          Reporting
        </span>
      ),
      icon: withTooltip(<img className="mr-2" style={{ height: '20px', width: '20px' }} alt="dropdown icon" src={ReportingMenuIcon} />, 'Reporting', 'reporting'),
      command: () => {
        setActiveItem('reports');
        navigate('reports');
      },
      className: currPathName === 'reports' && 'active',
      visible: hasPermission('menu_reports', role)
    },
    {
      id: 'help_center',
      label: (
        <span style={{
          fontFamily: 'Inter',
          fontWeight: 500,
          fontSize: '14px'
        }}
        >
          Help Center
        </span>
      ),
      icon: withTooltip(<img className="mr-2" style={{ height: '20px', width: '20px' }} alt="dropdown icon" src={HelpCenterMenuIcon} />, 'Help Center', 'Help Center'),
      command: () => {
        navigate('/help-center');
        // window.location.href = 'mailto:lmr.support@cyrino.com';
      },
      className: currPathName === 'help-center' && 'active',
      visible: hasPermission('menu_helpcenter', role)
    }
  ];

  useEffect(() => {
    if (['annotation-studio'].includes(currPathName)) {
      setCollapseSidebar(true);
    }
  }, [currPathName]);

  useEffect(() => {
    if (activeItem !== 'workflows' && activeItem !== 'home' && activeItem !== 'annotation-studio') {
      localStorage.removeItem('lastWorkflowClickedRow');
    }

    if (activeItem !== 'home' && activeItem !== 'annotation-studio') {
      localStorage.removeItem('dashbordSorting');
    }
    if (activeItem === 'workflows' && localStorage.getItem('lastclickFrom') === 'dashboard') {
      localStorage.removeItem('lastclickFrom');
      localStorage.removeItem('lastWorkflowClickedRow');
      localStorage.removeItem('dashbordSorting');
    }
  }, [activeItem]);

  return (
    <div
      role="sidebar"
      className={classNames({
        'sidebar-container h-screen hidden lg:block flex-shrink-0 absolute lg:static left-0 top-0 z-[50] overflow-visible': true,
        'collapsed-menu': collapseSidebar,
        'w-50': !collapseSidebar,
        'w-18': collapseSidebar

      })}
    >
      <div className="flex flex-column h-full sidebar-inner">
        {/*
        <div className="flex align-items-center px-5 flex-shrink-0 sidebar-logo">
          <img src={LogoImg} width="100" alt="Logo" />
        </div>
        */}

        <div className="panel-scroll overflow-y-auto">
          <PanelMenu model={items} className="panelmenu-main" />
        </div>

        {/*
        {!collapseSidebar ? (
          <div className="mt-auto px-4 text-white text-xs flex items-center gap-2" style={{ paddingBottom: '88px' }}>
            <span className="sidePanelText">Powered by</span>
            <img src={ChaiLabs} alt="CHAI Labs" className="h-4" style={{ width: '51px' }} />
          </div>
        ) : (
          <div className="mt-auto text-white flex items-center " style={{ paddingBottom: '88px', flexDirection: 'column', fontSize: '10px', lineHeight: '1.2', paddingLeft: '8px' }}>
            <span className="mb-1 whitespace-nowrap">Powered by</span>
            <img src={ChaiLabs} alt="CHAI Labs" className="h-5" style={{ width: '40px' }} />
          </div>
        )}
        */}
      </div>
    </div>
  );
};

export default Sidebar;
